/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
  getCOGS,
} from 'api/catalogos/index';
import {
  getPercepcionesClasificadoresDeObjetoDeGasto,
  postPercepcionClasificadorDeObjetoDeGasto,
  deletePercepcionClasificadorDeObjetoDeGasto,
  patchPercepcionClasificadorDeObjetoDeGasto,
  getPercepcionClasificadorDeObjetoDeGasto,
} from 'api/nominas/percepciones/percepcionesClasificadoresDeObjetoDeGasto';
import { checkToken } from 'api/utils';

export const percepcionClasificadorDeObjetoDeGastoPermission = {
  permissionModel: 'percepcionclasificadordeobjetodegasto',
  catalogsModels: [],
  tabsModels: [],
};

const PercepcionClasificadorDeObjetoDeGasto = ({
  permission,
  selected,
  estatusAutorizadoInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [clasificadores, setClasificadores] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPercepcionesClasificadoresDeObjetoDeGasto({ percepcion: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _clasificadores,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getCOGS(),
        getPercepcionesClasificadoresDeObjetoDeGasto({ percepcion: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setClasificadores(_clasificadores);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _percepcion = await getPercepcionClasificadorDeObjetoDeGasto(match.id);
    setTimeout(() => {
      if (_percepcion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_percepcion));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchPercepcionClasificadorDeObjetoDeGasto(key, { ...values, percepcion: selected }) : postPercepcionClasificadorDeObjetoDeGasto({ ...values, percepcion: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionClasificadorDeObjetoDeGasto(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clasificador por Objeto de Gasto',
      dataIndex: 'clasificacion_de_objeto_de_gasto',
      key: 'clasificacion_de_objeto_de_gasto',
      width: 300,
      render: (val) => {
        const _clasificador = clasificadores.find((e) => e.id === val);
        return _clasificador ? `${_clasificador.clave} - ${_clasificador.concepto}` : '';
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizadoInactivo}
          disableDelete={estatusAutorizadoInactivo}
          disableEdit={estatusAutorizadoInactivo}
          allowDoubleClick={estatusAutorizadoInactivo === false}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Clasificador por Objeto de Gasto`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clasificacion_de_objeto_de_gasto"
                label="Clasificador por Objeto de Gasto"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  keyProp="id"
                  labelProp="clave"
                  dataSource={clasificadores}
                  render={(value) => `${value?.clave} - ${value?.concepto}`}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Clasificador por Objeto de Gasto ${clasificadores.find((e) => e.id === form.getFieldValue('clasificacion_de_objeto_de_gasto'))?.concepto}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionClasificadorDeObjetoDeGasto;
